import {
  ComponentType,
  DashboardState,
  ESdChangeReason,
  IComponent,
  IComponentChild,
  IDisplayItem
} from "@kuika/kuika-cl-model";
import { COLUMN_COMPONENTS, SKIP_ROW_WRAP_SET } from "../../component-drop-constants";
import { validateComponentDrop } from "../../component-drop-validation";
import { applyDesignerRowColumns, injectIntoDesignerDictionary } from "../../designer-component-actions";
import { getValues } from "../../designer-context";
import type { ScreenDesignInstance } from "../../designer-context";
import { applyPropertyGridChangeWithoutCommit, isDataEditorProperty } from "../../designer-property-actions";
import { buildComponentValidation, type BuildComponentValidationInput } from "../../designer-validation-input-actions";
import { ScreenDesignHelper } from "../../screen-design-helper";
import { CHILD_ROLE_ANNOTATIONS, PROTECTED_TABLE_CHILDREN } from "../child-role-annotations";
import { getSdInstance } from "../helpers";
import { collectSubtreeActionRefs, sweepOrphanedScreenActions } from "../helpers/orphan-screen-action-sweep";
import {
  applyCssStylesToComponent,
  canApplyStylesToStructuralWrapper,
  normalizeStyleObjIds,
  STATE_KEYS,
  type StyleState
} from "../helpers/style-helpers";
import type { ToolHandlerGroup } from "../types";

function collectDescendantIds(di: IDisplayItem): Set<string> {
  const ids = new Set<string>();
  const descendants = ScreenDesignHelper.getAllChildrenForDI(di);
  for (const d of descendants) {
    if (d.id != null) ids.add(d.id.toString());
  }
  return ids;
}

/**
 * Normalizes styleObj IDs on a display item and all its descendants.
 * Cleans up malformed IDs (e.g. "|undefined", "|", ["", null]) that can be
 * produced by addDefaultComponentStylings when component definitions lack
 * default styling values.
 */
function normalizeStyleObjTree(di: any): void {
  if (!di) return;
  if (di.styleObj) normalizeStyleObjIds(di.styleObj);
  if (di.children && Array.isArray(di.children)) {
    for (const child of di.children) {
      normalizeStyleObjTree(child);
    }
  }
}

interface ComponentStateStylesInput {
  hover?: Record<string, unknown> | null;
  pressed?: Record<string, unknown> | null;
  focused?: Record<string, unknown> | null;
  disabled?: Record<string, unknown> | null;
}

type StateStylesInput = ComponentStateStylesInput | null | undefined;

/**
 * Applies base + per-state styles to a freshly added (or targeted) component
 * via `applyCssStylesToComponent`. The base call lands first, then each
 * non-empty state bucket is written through with `options.state` so the
 * helper's state pipeline (`liftSectionChangesToState`) routes the change
 * into `{state}Styles` and reverts base. All calls use `shouldCommit:false`
 * so the caller batches a single commit at the end. Unsupported props per
 * state surface as `skippedProperties` warnings via the helper's normal
 * channel — preserved here so the LLM sees them.
 *
 * `styles` is `unknown` because translator-emitted payloads carry their JSON
 * validation upstream and the inner helper accepts a `CssStyleInput` (which
 * is itself a loose object record). We re-validate the object-ness locally
 * before forwarding.
 */
function applyComponentStylesWithStates(
  sd: ScreenDesignInstance,
  di: IDisplayItem,
  kuikaId: string,
  styles: unknown,
  stateStyles: StateStylesInput
): void {
  if (styles && typeof styles === "object" && Object.keys(styles as object).length > 0) {
    applyCssStylesToComponent(sd, di, kuikaId, styles as Record<string, unknown>, { shouldCommit: false });
  }
  if (!stateStyles || typeof stateStyles !== "object") return;
  for (const state of STATE_KEYS) {
    const bucket = (stateStyles as Record<StyleState, unknown>)[state];
    if (!bucket || typeof bucket !== "object" || Object.keys(bucket as object).length === 0) continue;
    applyCssStylesToComponent(sd, di, kuikaId, bucket as Record<string, unknown>, {
      shouldCommit: false,
      state
    });
  }
}

interface ComponentValidationsInput {
  required?: boolean;
  minLength?: number;
  maxLength?: number;
  minValue?: number;
  maxValue?: number;
}

/**
 * Applies markup-authored input validations (`validate="required minLength:3"`)
 * to a freshly added component by mutating `di.item.validations` through the
 * shared no-commit builder `buildComponentValidation`. NO commit here — the add
 * handler batches a single commit at the end, mirroring
 * `applyComponentStylesWithStates`' `shouldCommit:false`.
 *
 * Soft-degrades: a component that does not support validation (hasValidate
 * false) or a rule the builder rejects is logged and skipped — the component
 * add NEVER fails because of a validation. The common "validate on a non-input"
 * case is already surfaced to the model as a translator ParseWarning, so the
 * residual skip surface here is near-zero.
 *
 * `validations` is `unknown` because the translator-emitted payload carries its
 * JSON validation upstream; object-ness and each field type are re-checked here.
 */
function applyComponentValidations(sd: ScreenDesignInstance, di: IDisplayItem, validations: unknown): void {
  if (!validations || typeof validations !== "object") return;
  const v = validations as ComponentValidationsInput;

  const rules: BuildComponentValidationInput[] = [];
  if (v.required === true) rules.push({ validationType: "required" });
  if (typeof v.minLength === "number") rules.push({ validationType: "minLength", value: v.minLength });
  if (typeof v.maxLength === "number") rules.push({ validationType: "maxLength", value: v.maxLength });
  if (typeof v.minValue === "number") rules.push({ validationType: "minValue", value: v.minValue });
  if (typeof v.maxValue === "number") rules.push({ validationType: "maxValue", value: v.maxValue });

  for (const rule of rules) {
    const built = buildComponentValidation(sd, di, rule);
    if ("error" in built) {
      console.warn(
        `[MCP/Validations] skipped "${rule.validationType}" on ${di.item?.name ?? "component"}: ${built.error}`
      );
      continue;
    }
    if (!di.item.validations) di.item.validations = [];
    di.item.validations.push(built.validation);
  }
}

/**
 * Screen-contract channels, as a tool payload carries them: `contractId` (our
 * own identity for the component) plus the three references.
 *
 * Kept as one shape with one applier because the channels are set and cleared
 * together in practice, and because a per-site `if (comp.dataRef)` quadruplet is
 * exactly how the friendlyName write path grew two copies that then drifted (see
 * the two `newChild.friendlyName` assignments below). `friendlyName` itself is
 * NOT handled here — the system owns that field and derives it from the label.
 */
interface IContractChannelPayload {
  contractId?: unknown;
  dataRef?: unknown;
  fieldRef?: unknown;
  interactionRef?: unknown;
}

const CONTRACT_CHANNEL_KEYS: ReadonlyArray<"contractId" | "dataRef" | "fieldRef" | "interactionRef"> = [
  "contractId",
  "dataRef",
  "fieldRef",
  "interactionRef"
];

/**
 * Copy the present contract channels from a payload onto a display item.
 *
 * An ABSENT key leaves the existing value untouched — a configure that only sets
 * `interactionRef` must not erase a `dataRef` stamped by an earlier pass. An
 * EMPTY STRING clears the channel, which is the only way to un-stamp one once
 * the wiring module decides a component no longer owns a slot. The value is
 * stored verbatim apart from trimming: these are exact contract keys, so no
 * folding, no casing, no sanitization.
 * Returns whether anything changed, so callers can skip a pointless commit.
 */
function applyContractChannels(di: IDisplayItem, payload: IContractChannelPayload): boolean {
  let changed: boolean = false;
  for (const key of CONTRACT_CHANNEL_KEYS) {
    const raw: unknown = payload[key];
    if (raw === undefined || raw === null) continue;
    if (typeof raw !== "string") continue;
    const next: string | undefined = raw.trim().length > 0 ? raw.trim() : undefined;
    if (di[key] === next) continue;
    di[key] = next;
    changed = true;
  }
  return changed;
}

function deleteSingleComponent(
  sd: any,
  id: string,
  deletedSet: Set<string>,
  releasedActionIds: Set<string>
): {
  success?: boolean;
  skipped?: boolean;
  error?: string;
  reason?: string;
  kuikaId: string;
  componentType?: string;
  alias?: string;
} {
  if (deletedSet.has(id)) {
    return { skipped: true, kuikaId: id, reason: "already deleted as descendant of a previously deleted component" };
  }

  if (id === "-1") {
    return { success: false, error: "Cannot delete the Body section", kuikaId: id };
  }

  const dict = sd.screenProvider.state.screenDesignDictionary;
  const di = dict[parseInt(id, 10)];

  if (!di || !di.item) {
    return { skipped: true, kuikaId: id, reason: "component not found" };
  }

  const componentName = di.item.name;

  // Guard: protected structural table children
  if (componentName && PROTECTED_TABLE_CHILDREN.has(componentName)) {
    return {
      success: false,
      error: `Cannot delete ${componentName} (kuikaId ${id}) — structural part of parent table.`,
      kuikaId: id
    };
  }

  // Guard: fixed children
  if (componentName && di.parentId != null) {
    const parentDi: IDisplayItem | undefined = dict[di.parentId];
    if (parentDi?.item) {
      const components: IComponent[] | undefined =
        sd.projectProvider?.state?.project?.componentSetObj?.components ||
        sd.state?.project?.componentSetObj?.components;
      if (components) {
        const parentDef = components.find((c: IComponent) => c.id === parentDi.item.id);
        if (parentDef?.childrenInfo) {
          const childDef = parentDef.childrenInfo.find((c: IComponentChild) => c.name === componentName);
          if (childDef?.fixed) {
            return {
              success: false,
              error: `Cannot delete ${componentName} (kuikaId ${id}) — fixed child of ${parentDi.item.name}.`,
              kuikaId: id
            };
          }
        }
      }
    }
  }

  // Guard: last column in table row/header
  if (componentName && COLUMN_COMPONENTS.has(componentName) && di.parentId != null) {
    const parentDi: IDisplayItem | undefined = dict[di.parentId];
    if (parentDi?.children) {
      const columnChildren = parentDi.children.filter(
        (c: IDisplayItem) => c.item?.name && COLUMN_COMPONENTS.has(c.item.name)
      );
      if (columnChildren.length <= 1) {
        return {
          success: false,
          error: `Cannot delete the last column in ${parentDi.item?.name} (kuikaId ${di.parentId}).`,
          kuikaId: id
        };
      }
    }
  }

  // Capture component info before deletion
  const deletedComponentType = componentName || "unknown";
  const deletedAlias = di.friendlyName || di.title || undefined;

  // Collect descendant IDs before deletion
  const descendantIds = collectDescendantIds(di);

  // KOA-18206 — which screen-level action instances this subtree was referencing.
  // Must be read BEFORE the splice; afterwards the symbols are gone and a
  // bootstrapped `pageInit` datasource load would be left unreachable forever.
  for (const actionId of collectSubtreeActionRefs(di)) {
    releasedActionIds.add(actionId);
  }

  // Perform deletion
  sd.displayItemProvider.deleteItemByKuikaId(id);

  // Mark this + descendants as deleted
  deletedSet.add(id);
  for (const descId of descendantIds) {
    deletedSet.add(descId);
  }

  return { success: true, kuikaId: id, componentType: deletedComponentType, alias: deletedAlias };
}

function safeParse(val: any): any {
  if (val == null) return val;
  if (typeof val === "string") {
    try {
      return JSON.parse(val);
    } catch {
      return val;
    }
  }
  return val;
}

function summarizeBatchResults(results: any[]) {
  const failedCount = results.filter((r) => r.error != null).length;
  const successCount = results.length - failedCount;
  if (failedCount === 0) {
    return { success: true, successCount, failedCount: 0 };
  }
  const msgs = results
    .filter((r) => r.error != null)
    .slice(0, 3)
    .map((r) => r.error);
  return {
    success: successCount > 0,
    successCount,
    failedCount,
    error: `${failedCount} of ${results.length} failed: ${msgs.join("; ")}`
  };
}

/**
 * After addChildItemByComponentName, the screenDesignDictionary is rebuilt
 * asynchronously (Promise.then + React setState). Properties/styles applied
 * in the same synchronous handler call would fail because
 * applyPropertyGridChangesToDisplay looks up the component by kuikaId in
 * the dictionary and the new component isn't there yet.
 *
 * This function injects the newly added display item (and all its
 * auto-created children) into the current dictionary so that subsequent
 * applyPropertyGridChangesToDisplay calls can find them.
 */
export function injectIntoDictionary(di: any, dict: any): void {
  injectIntoDesignerDictionary(di, dict);
}

interface IAnnotatedChildInfo {
  kuikaId: string | undefined;
  role?: string;
}

/** Annotate a child display item with role info from CHILD_ROLE_ANNOTATIONS. */
function annotateChild(di: IDisplayItem): IAnnotatedChildInfo {
  const compType = di.item?.name;
  const base: IAnnotatedChildInfo = {
    kuikaId: di.id?.toString()
  };
  if (!compType) return base;
  const annotation = CHILD_ROLE_ANNOTATIONS[compType];
  if (!annotation) return base;
  return {
    ...base,
    role: annotation.role
  };
}

interface INewChildInfo {
  kuikaId: string | undefined;
  componentType: string | undefined;
  friendlyName: string | null;
  children: IAnnotatedChildInfo[];
  warnings?: string[];
}

/** Build annotated newChildInfo for an added component and its auto-created children. */
function buildNewChildInfo(newChild: IDisplayItem): INewChildInfo {
  const children = (newChild.children || []).map((c: IDisplayItem) => annotateChild(c));

  return {
    kuikaId: newChild.id?.toString(),
    componentType: newChild.item?.name,
    friendlyName: newChild.friendlyName || null,
    children
  };
}

/**
 * Returns true when the current dashboard state is report designer (state 17).
 * In report designer mode only components with isReportDesignComponent=true are allowed.
 * In regular designer mode (state 1) only components with isReportDesignComponent=false are allowed.
 */
function isReportDesignMode(sd: any): boolean {
  const dashboardState = sd.designSituationProvider?.state?.dashboardState ?? sd.state?.dashboardState ?? null;
  return dashboardState === DashboardState.reportDesigner;
}

/**
 * Validates that a component is allowed in the current design mode.
 * Returns an error string if the component is not allowed, or null if valid.
 */
function validateComponentForDesignMode(sd: any, componentName: string): string | null {
  const reportMode = isReportDesignMode(sd);
  const components =
    sd.projectProvider?.state?.project?.componentSetObj?.components || sd.state?.project?.componentSetObj?.components;
  if (!components) return null; // can't validate, let downstream handle it

  const matchingComponents = components.filter((c: any) => c.name === componentName && c.isDeprecated === false);
  if (matchingComponents.length === 0) return null; // component not found, let downstream handle it

  // Check if ANY variant of this component is valid for the current design mode
  const validForMode = matchingComponents.some((c: any) =>
    reportMode ? c.isReportDesignComponent === true : !c.isReportDesignComponent
  );
  if (!validForMode) {
    return reportMode
      ? `Component "${componentName}" is not available in report designer mode. Only report design components can be used here.`
      : `Component "${componentName}" is a report design component and cannot be used in the screen designer. It is only available in the report designer (dashboard state 17).`;
  }

  return null;
}

/**
 * Ensures that a Header (kuikaId -2) or Footer (kuikaId -3) display item exists.
 * If the targeted section doesn't exist, it is auto-created following the same
 * pattern as createFixedPanelDI in drag-drop-helper.ts.
 *
 * Returns null on success (or if not a header/footer target), or an error object.
 */
function ensureHeaderOrFooterExists(sd: any, targetKuikaId: string): { error: string } | null {
  const targetId = parseInt(targetKuikaId, 10);
  if (targetId !== -2 && targetId !== -3) return null;

  const screenDesign = sd.screenProvider.getScreenDesign();
  const dict = sd.screenProvider.state.screenDesignDictionary;

  // Check if already exists
  const existing = screenDesign.displayItems.find((di: any) => di.id === targetId);
  if (existing) return null;

  // Determine component type
  const componentType = targetId === -2 ? ComponentType.fixedHeader : ComponentType.fixedFooter;
  const components =
    sd.projectProvider?.state?.project?.componentSetObj?.components || sd.state?.project?.componentSetObj?.components;
  if (!components) return { error: "Component definitions not found" };

  const reportMode = isReportDesignMode(sd);
  const compDef = components.find(
    (c: any) =>
      c.componentType === componentType &&
      (reportMode ? c.isReportDesignComponent === true : !c.isReportDesignComponent)
  );
  if (!compDef) {
    return { error: `${targetId === -2 ? "Header" : "Footer"} component definition not found` };
  }

  // Create using ScreenDesignHelper (same pattern as createFixedPanelDI)
  const newComponent = ScreenDesignHelper.createNewComponentById(compDef.id, sd);
  if (!newComponent) {
    return { error: `Failed to create ${targetId === -2 ? "Header" : "Footer"} component` };
  }

  const newComponentDefinition = ScreenDesignHelper.getComponentDefinition(newComponent, components);

  let newDI = ScreenDesignHelper.createNewDisplayItemByComponent(
    screenDesign,
    dict,
    undefined,
    newComponent,
    components
  );
  newDI.id = targetId;

  if (newComponentDefinition) {
    newDI = ScreenDesignHelper.addDefaultComponentStylings(newDI, newComponentDefinition, sd.defaultStyleProvider);
    normalizeStyleObjTree(newDI);
  }

  // Handle absolute design default height
  if (screenDesign.isAbsoluteDesign === true) {
    if (newDI.styleObj?.position && !newDI.styleObj.position.height) {
      newDI.styleObj.position.height = 96;
    }
  }

  screenDesign.displayItems.splice(0, 0, newDI);
  sd.screenProvider.setScreenDesign(screenDesign, 1, targetId.toString());
  injectIntoDictionary(newDI, dict);

  return null;
}

/**
 * After a new component is appended at the end of a parent's children,
 * reposition it before or after a specified sibling.
 */
function repositionChild(
  parentDi: any,
  newChild: any,
  insertBeforeKuikaId?: string,
  insertAfterKuikaId?: string
): { repositioned: boolean; error?: string; newIndex?: number } {
  if (!insertBeforeKuikaId && !insertAfterKuikaId) {
    return { repositioned: false };
  }
  if (insertBeforeKuikaId && insertAfterKuikaId) {
    return { repositioned: false, error: "insertBeforeKuikaId and insertAfterKuikaId are mutually exclusive" };
  }

  const siblings = parentDi.children;
  if (!siblings || siblings.length < 2) {
    return { repositioned: false };
  }

  // The new child is currently the last element
  const currentIdx = siblings.indexOf(newChild);
  if (currentIdx === -1) {
    return { repositioned: false, error: "New component not found in parent children" };
  }

  const targetId = insertBeforeKuikaId || insertAfterKuikaId;
  const targetDi = siblings.find((s: any) => s.id?.toString() === targetId);
  if (!targetDi) {
    return { repositioned: false, error: `Sibling "${targetId}" not found in parent's children` };
  }

  // Remove from current (last) position
  siblings.splice(currentIdx, 1);

  // Find target in the now-shorter array and calculate insertion point
  const targetIdx = siblings.indexOf(targetDi);
  const insertIdx = insertBeforeKuikaId ? targetIdx : targetIdx + 1;

  siblings.splice(insertIdx, 0, newChild);

  return { repositioned: true, newIndex: insertIdx };
}

// getKeyProperties removed — LLM already knows key properties from prompt context

const COMPONENT_HINTS: Record<string, string> = {
  Image: 'Set the "value" property to an image URL (e.g. "https://example.com/photo.jpg") to display a remote image.',
  Thumbnail: 'Set the "value" property to an image URL to display a zoomable remote image.'
};

function getComponentHint(componentName: string): string | undefined {
  return COMPONENT_HINTS[componentName];
}

/**
 * Post-creation quality warnings. Returns an array of actionable warnings
 * that are appended to the tool result so the model can self-correct.
 */
function getCreationWarnings(componentName: string, styles: any, comp: any): string[] {
  const warnings: string[] = [];

  // Warning: backgroundColor without padding
  if (styles && typeof styles === "object") {
    const hasBg = styles.backgroundColor != null;
    const hasPadding =
      styles.padding != null ||
      styles.paddingTop != null ||
      styles.paddingRight != null ||
      styles.paddingBottom != null ||
      styles.paddingLeft != null;
    if (hasBg && !hasPadding) {
      warnings.push(
        `DEFECT: ${componentName} has backgroundColor but NO padding. Text touches edges. Add padding (8-16px for chips, 20-32px for cards).`
      );
    }
  }

  // Warning: Image with both width and height set
  if (componentName === "Image" && styles && typeof styles === "object") {
    const hasWidth = styles.width != null;
    const hasHeight = styles.height != null;
    if (hasWidth && hasHeight) {
      warnings.push(
        `DEFECT: Image has both width AND height set — this distorts the image. Set only ONE dimension and let aspect ratio handle the other.`
      );
    }
  }

  // Warning: HorizontalStack used as layout container (has VerticalStack-named children pattern)
  // This is detected when the model names the HStack like a "Row" or "Split"
  if (componentName === "HorizontalStack") {
    const name = (comp.friendlyName || "").toLowerCase();
    if (name.includes("row") || name.includes("split") || name.includes("grid") || name.includes("column")) {
      warnings.push(
        `WARNING: HorizontalStack "${comp.friendlyName}" sounds like a layout container. HorizontalStack is NOT responsive. Use Row with columns[] for multi-column layouts. If the parent is a VerticalStack, change it to Panel (Panel accepts Row children).`
      );
    }
  }

  return warnings;
}

export function applyRowColumns(
  sd: any,
  newChild: any,
  newChildId: string,
  columnsParam: any,
  tabletColumnsParam?: number,
  mobileColumnsParam?: number
) {
  applyDesignerRowColumns(sd, newChild, newChildId, columnsParam, tabletColumnsParam, mobileColumnsParam);
}

// --- Auto-wrap & drop-validation helpers (mirrors drag-drop-helper.ts rules) ---

/**
 * Checks if a component is a structural wrapper (Row/Col that is part of
 * the auto-wrap structure in the Body). These should not be styled.
 */
const STRUCTURAL_NAMES = new Set(["Row", "Col", "Column"]);

function isStructuralWrapper(di: any, dict: any): boolean {
  if (!di || !STRUCTURAL_NAMES.has(di.item?.name)) return false;
  if (di.item?.name === "Row" && di.parentId === -1) return true;
  if ((di.item?.name === "Col" || di.item?.name === "Column") && di.parentId != null) {
    const parentDi = dict[di.parentId];
    if (parentDi?.item?.name === "Row" && parentDi.parentId === -1) return true;
  }
  return false;
}

// SKIP_ROW_WRAP_SET imported from component-drop-constants.ts

/**
 * Determines whether a component added to the given target should be
 * automatically wrapped in Row > Col (matching the drag-drop behaviour).
 * Only wraps when the target is the Body/Root DI (id === -1) and the
 * screen is NOT in absolute-design mode.
 */
function shouldAutoWrapInRowCol(sd: any, targetKuikaId: string, componentName: string): boolean {
  if (SKIP_ROW_WRAP_SET.has(componentName)) return false;

  const screenDesign = sd.screenProvider.getScreenDesign();
  if (screenDesign?.isAbsoluteDesign) return false;

  // Body/Root DI has id = -1 (from DEFAULT_LAYOUT in helpers.ts)
  const targetId = parseInt(targetKuikaId, 10);
  if (targetId !== -1) return false;

  return true;
}

function isChildOfForm(di: any, dict: any): boolean {
  if (!di) return false;
  let current = di.parentId != null ? dict[di.parentId] : null;
  while (current) {
    if (current.item?.name === "Form") return true;
    if (!current.parentId || current.id === -1 || current.parentId === -1) break;
    current = dict[current.parentId];
  }
  return false;
}

/**
 * Post-creation adjustments matching drag-drop behavior in
 * dragDropByCreateItemMessage (drag-drop-helper.ts:731-740, 502-515, 1012-1014).
 *
 * Called after addChildItemByComponentName() which already handles:
 * - addDefaultComponentStylings() (text/border/position/shadow/fill IDs)
 * - addChildForItemContainer() (auto-create table columns, tab panes, etc.)
 * - Table column special sizing (height=60, width=200/height=40)
 */
function applyPostCreationAdjustments(sd: any, newChild: any, targetKuikaId: string): void {
  const dict = sd.screenProvider.state.screenDesignDictionary;
  const targetDi = dict[parseInt(targetKuikaId, 10)];
  const componentName = newChild?.item?.name;

  // Gap 2: Menu mode = "horizontal" when inside Header/Footer, "vertical" otherwise
  // (drag-drop-helper.ts lines 502-515)
  if (componentName === "Menu" && newChild?.item?.properties) {
    const isInHeaderFooter = ScreenDesignHelper.isParentHeaderOrFooter(targetDi, dict);
    const mode = isInHeaderFooter ? "horizontal" : "vertical";
    const modeProp = newChild.item.properties.find((p: any) => p.name === "mode");
    if (modeProp?.symbol) {
      modeProp.symbol.fixedValue = mode;
    }
  }

  // Gap 3: Panel default height=100 in absolute design
  // (drag-drop-helper.ts lines 1012-1014)
  const screenDesign = sd.screenProvider.getScreenDesign();
  if (componentName === "Panel" && newChild?.styleObj?.position && screenDesign?.isAbsoluteDesign === true) {
    newChild.styleObj.position.height = 100;
  }
}

/**
 * Validates whether a component can be dropped into the specified target.
 * Thin wrapper around the shared validateComponentDrop() — resolves SD-specific
 * lookups (dictionary, component defs, form ancestry) then delegates.
 *
 * Parallel implementation in drag-drop-helper.ts → getElementIsDroppable().
 */
function validateDrop(sd: any, targetKuikaId: string, componentName: string): string | null {
  const dict = sd.screenProvider.state.screenDesignDictionary;
  const targetDi = dict[parseInt(targetKuikaId, 10)];
  if (!targetDi) return `Target container with kuikaId "${targetKuikaId}" not found.`;

  const targetName = targetDi.item?.name;
  const componentDefs = sd.projectProvider?.state?.project?.componentSetObj?.components;
  const sourceDef = componentDefs?.find((c: any) => c.name === componentName);
  const targetDef = componentDefs?.find((c: any) => c.id === targetDi.item?.id);
  const screenDesign = sd.screenProvider.getScreenDesign();

  return validateComponentDrop({
    targetName,
    componentName,
    targetComponentDef: targetDef,
    sourceComponentDef: sourceDef,
    isTargetInteractionDisabled: !!targetDi.isChildrenInteractionDisabled,
    isTargetInsideForm: isChildOfForm(targetDi, dict),
    isAbsoluteDesign: !!screenDesign?.isAbsoluteDesign
  });
}

/**
 * Cleans up empty Row/Col wrappers left behind after a component is removed
 * from a Col. If the Col is empty and its parent Row has all empty Cols,
 * the entire Row is removed. Otherwise only the empty Col is removed.
 *
 * Returns an array of removed wrapper kuikaIds, or null if nothing was cleaned.
 */
function cleanupEmptyRowColWrappers(oldParentDi: any, dict: any, screenDesign: any): string[] | null {
  if (!oldParentDi || oldParentDi.item?.name !== "Col") return null;
  const colIsEmpty = !oldParentDi.children || oldParentDi.children.length === 0;
  if (!colIsEmpty) return null;

  const rowDi = oldParentDi.parentId != null ? dict[oldParentDi.parentId] : null;
  if (!rowDi || rowDi.item?.name !== "Row" || !rowDi.children) return null;

  const allColsEmpty = rowDi.children.every(
    (col: any) => col.item?.name === "Col" && (!col.children || col.children.length === 0)
  );

  if (allColsEmpty) {
    // Remove the entire Row from its parent
    const rowParentDi = rowDi.parentId != null ? dict[rowDi.parentId] : null;
    let rowParentChildren: any[] | null = null;
    if (rowParentDi && rowParentDi.children) {
      rowParentChildren = rowParentDi.children;
    } else if (rowDi.parentId === -1 || rowDi.parentId == null) {
      rowParentChildren = screenDesign.displayItems;
    }
    if (rowParentChildren) {
      const rowIdx = rowParentChildren.indexOf(rowDi);
      if (rowIdx !== -1) {
        rowParentChildren.splice(rowIdx, 1);
        for (const col of rowDi.children) {
          if (col.id != null) delete dict[col.id];
        }
        delete dict[rowDi.id];
        return [rowDi.id?.toString()];
      }
    }
  } else {
    // Only remove this empty Col from the Row
    const colIdx = rowDi.children.indexOf(oldParentDi);
    if (colIdx !== -1) {
      rowDi.children.splice(colIdx, 1);
      if (oldParentDi.id != null) delete dict[oldParentDi.id];
      return [oldParentDi.id?.toString()];
    }
  }

  return null;
}

/**
 * Wraps a component in Row > Col by adding a Row to the body first,
 * then adding the actual component into the Col.
 * Returns { rowDi, colDi, actualDi, actualId } or null if wrapping failed.
 *
 * NOTE: Parallel implementation in drag-drop-helper.ts → wrapDIWithDefaultRow().
 * Not shared because MCP mutates in-place synchronously while drag-drop
 * calls setScreenDesign() asynchronously.
 */
/**
 * Auto-wraps a component in a Col when being added directly into a Row.
 * Row only accepts Col children — this creates a full-width Col and adds
 * the component inside it, matching the designer's drag-drop behavior.
 */
function addWithColWrap(
  sd: any,
  dict: any,
  rowKuikaId: string,
  componentName: string
): { colDi: any; actualDi: any; actualId: string } | null {
  const rowDi = dict[parseInt(rowKuikaId, 10)];
  if (!rowDi) return null;

  // 1. Add Col to Row
  const rowChildrenBefore = rowDi.children?.length || 0;
  sd.displayItemProvider.addChildItemByComponentName(rowKuikaId, "Col");
  const rowChildrenAfter = rowDi.children || [];
  if (rowChildrenAfter.length <= rowChildrenBefore) return null;

  const colDi = rowChildrenAfter[rowChildrenAfter.length - 1];
  const colId = colDi.id?.toString();
  normalizeStyleObjTree(colDi);
  injectIntoDictionary(colDi, dict);

  // 2. Add actual component into the Col
  const colChildrenBefore = colDi.children?.length || 0;
  sd.displayItemProvider.addChildItemByComponentName(colId, componentName);
  const colChildrenAfter = colDi.children || [];
  if (colChildrenAfter.length <= colChildrenBefore) return null;

  const actualDi = colChildrenAfter[colChildrenAfter.length - 1];
  const actualId = actualDi.id?.toString();
  normalizeStyleObjTree(actualDi);
  injectIntoDictionary(actualDi, dict);

  return { colDi, actualDi, actualId };
}

function addWithRowColWrap(
  sd: any,
  dict: any,
  bodyKuikaId: string,
  componentName: string
): { rowDi: any; colDi: any; actualDi: any; actualId: string } | null {
  const bodyDi = dict[parseInt(bodyKuikaId, 10)];
  if (!bodyDi) return null;

  // 1. Add Row to body
  const bodyChildrenBefore = bodyDi.children?.length || 0;
  sd.displayItemProvider.addChildItemByComponentName(bodyKuikaId, "Row");
  const bodyChildrenAfter = bodyDi.children || [];
  if (bodyChildrenAfter.length <= bodyChildrenBefore) return null;

  const rowDi = bodyChildrenAfter[bodyChildrenAfter.length - 1];
  const rowId = rowDi.id?.toString();
  normalizeStyleObjTree(rowDi);
  injectIntoDictionary(rowDi, dict);

  // 2. Set Row columns to [24] (single full-width Col)
  applyRowColumns(sd, rowDi, rowId, [24]);

  // 3. Get the auto-created Col (Row's first child after setColumnsNumber)
  const colDi = rowDi.children?.[0];
  if (!colDi) return null;
  normalizeStyleObjTree(colDi);
  injectIntoDictionary(colDi, dict);
  const colId = colDi.id?.toString();

  // 4. Add actual component to Col
  const colChildrenBefore = colDi.children?.length || 0;
  sd.displayItemProvider.addChildItemByComponentName(colId, componentName);
  const colChildrenAfter = colDi.children || [];
  if (colChildrenAfter.length <= colChildrenBefore) return null;

  const actualDi = colChildrenAfter[colChildrenAfter.length - 1];
  const actualId = actualDi.id?.toString();
  normalizeStyleObjTree(actualDi);
  injectIntoDictionary(actualDi, dict);

  return { rowDi, colDi, actualDi, actualId };
}

export const componentTools: ToolHandlerGroup = {
  sync: {
    ui_structure_add_components: (input) => {
      const components = safeParse(input.components);
      if (!components || !Array.isArray(components) || components.length === 0) {
        return { error: "components array is required and must not be empty" };
      }

      const sd = getSdInstance();
      const dict = sd.screenProvider.state.screenDesignDictionary;
      const sdState = getValues(sd);
      const results: any[] = [];

      // Suppress intermediate setScreenDesign calls during the batch loop.
      // Each addChildItemByComponentName internally calls setScreenDesign which
      // triggers a full React reconciliation (~114 re-renders). For a batch of
      // 20 components with Row/Col wrapping, that's ~60 calls × 114 = ~7000
      // unnecessary re-renders. We replace it with a no-op during the loop and
      // do a single setScreenDesign at the end (line below the loop).
      const realSetScreenDesign = sd.screenProvider.setScreenDesign.bind(sd.screenProvider);
      sd.screenProvider.setScreenDesign = () => {};

      // Placeholder ref map: "$0" → kuikaId of the 0th result, "$1" → 1st, etc.
      // This lets the model build nested hierarchies in a single call:
      //   [{ targetKuikaId: "-1", componentName: "Panel" },
      //    { targetKuikaId: "$0", componentName: "Label" }]
      // The Label targets the Panel created in the first item.
      const refMap = new Map<string, string>();

      try {
        for (let compIdx = 0; compIdx < components.length; compIdx++) {
          const comp = components[compIdx];
          let { targetKuikaId, componentName, friendlyName } = comp;
          const properties = safeParse(comp.properties);
          const styles = safeParse(comp.styles);
          const stateStyles = (comp.stateStyles as StateStylesInput) ?? undefined;
          const validations: unknown = comp.validations;

          // Resolve @alias refs (e.g., "@pageStack") using the same refMap as $N
          if (targetKuikaId && targetKuikaId.startsWith("@")) {
            const aliasName = targetKuikaId.slice(1);
            const resolved = refMap.get(`@${aliasName}`);
            if (!resolved) {
              results.push({
                error:
                  `Alias ref "${targetKuikaId}" not found. ` +
                  `If this alias was created in a previous add call, it should have been resolved server-side. ` +
                  `Use the actual kuikaId from the previous call's result instead.`,
                componentName
              });
              continue;
            }
            targetKuikaId = resolved;
          }

          // Resolve placeholder refs like "$0", "$1", "$5.0" (auto-child) to actual kuikaIds
          if (targetKuikaId && targetKuikaId.startsWith("$")) {
            let resolved = refMap.get(targetKuikaId);

            // If $N resolved to a Row and the component is not a Col, auto-redirect to first Col child
            if (resolved && !targetKuikaId.includes(".") && !COLUMN_COMPONENTS.has(componentName)) {
              const resolvedDi = dict[parseInt(resolved, 10)];
              if (resolvedDi?.item?.name === "Row" && resolvedDi.children?.length > 0) {
                const firstColId = resolvedDi.children[0].id?.toString();
                if (firstColId) {
                  resolved = firstColId;
                }
              }
            }

            // If $N resolved to a Table and the component IS a column, auto-redirect to the TableRow child.
            // The translator strips <TableRow> as structural and targets columns to $Table.
            // But the platform requires columns to be added to the TableRow, not the Table.
            if (resolved && COLUMN_COMPONENTS.has(componentName)) {
              const resolvedDi = dict[parseInt(resolved, 10)];
              if (resolvedDi?.item?.name === "Table" || resolvedDi?.item?.name === "ReportTable") {
                const tableRow = resolvedDi.children?.find(
                  (c: any) => c.item?.name === "TableRow" || c.item?.name === "ReportTableRow"
                );
                if (tableRow?.id) {
                  resolved = tableRow.id.toString();
                }
              }
            }

            if (!resolved) {
              results.push({
                error: `Placeholder ref "${targetKuikaId}" not found — referenced component may have failed`,
                componentName
              });
              continue;
            }
            targetKuikaId = resolved;
          }

          if (!targetKuikaId || !componentName) {
            results.push({ error: `Missing targetKuikaId or componentName`, input: comp });
            continue;
          }

          // Early check: if a literal kuikaId is not in the dictionary, provide actionable $N.M suggestions
          // This catches hallucinated IDs before they reach validateDrop with a generic "not found" error
          if (
            targetKuikaId !== "-1" &&
            targetKuikaId !== "-2" &&
            targetKuikaId !== "-3" &&
            !targetKuikaId.startsWith("$") &&
            !targetKuikaId.startsWith("@")
          ) {
            const targetNum = parseInt(targetKuikaId, 10);
            if (!isNaN(targetNum) && !dict[targetNum]) {
              const colSuggestions: string[] = [];
              for (const [ref, rid] of refMap.entries()) {
                if (ref.startsWith("$") && !ref.includes(".")) {
                  const rdi = dict[parseInt(rid, 10)];
                  if (rdi?.item?.name === "Row" && rdi.children?.length) {
                    const label = rdi.friendlyName ? ` "${rdi.friendlyName}"` : "";
                    for (let ci = 0; ci < rdi.children.length; ci++) {
                      const cid = rdi.children[ci].id?.toString();
                      if (cid) colSuggestions.push(`${ref}.${ci} (kuikaId: ${cid}, Col of Row${label})`);
                    }
                  }
                }
              }
              // Also list some existing components on the screen for context
              const existingHints: string[] = [];
              const SKIP_HINT = new Set(["Row", "Col", "Unknown", "HTML_DIV", "TableHeader", "TableRow"]);
              for (const key of Object.keys(dict).slice(0, 200)) {
                const c = dict[parseInt(key, 10)];
                if (c?.item?.name && !SKIP_HINT.has(c.item.name) && c.id != null) {
                  const fn = c.friendlyName ? `"${c.friendlyName}" ` : "";
                  existingHints.push(`${fn}${c.item.name}:${c.id}`);
                  if (existingHints.length >= 6) break;
                }
              }
              let msg = `Target container with kuikaId "${targetKuikaId}" not found.`;
              if (colSuggestions.length > 0) {
                msg += ` Use $N.M refs for Cols created in this batch: ${colSuggestions.join("; ")}.`;
              }
              if (existingHints.length > 0) {
                msg += ` Existing components on screen: ${existingHints.join(", ")}.`;
              }
              results.push({ error: msg, componentName, targetKuikaId });
              continue;
            }
          }

          // Validate component is allowed in current design mode (screen vs report)
          const designModeError = validateComponentForDesignMode(sd, componentName);
          if (designModeError) {
            results.push({ error: designModeError, componentName, targetKuikaId });
            continue;
          }

          // Auto-redirect Table column adds to the Table's TableRow child.
          // Mirrors the same redirect in the $N placeholder path above —
          // numeric kuikaIds need it too. Live regression: LLM emitted
          // <Table kuikaId="699642"><TableDefaultColumn .../></Table> to
          // add a column to an existing Table; without the redirect the
          // add hits validateDrop's parent-rule rejection (TableDefaultColumn
          // cannot be a direct child of Table) and the failure surfaces
          // as a non-"not found" drop error that the server-side aggregator
          // discards — the LLM sees `ok` and reports success while the
          // canvas is unchanged. Routing into the TableRow lets the
          // existing add path place the column where the platform expects it.
          if (
            COLUMN_COMPONENTS.has(componentName) &&
            !targetKuikaId.startsWith("$") &&
            !targetKuikaId.startsWith("@") &&
            targetKuikaId !== "-1" &&
            targetKuikaId !== "-2" &&
            targetKuikaId !== "-3"
          ) {
            const targetDi = dict[parseInt(targetKuikaId, 10)];
            if (targetDi?.item?.name === "Table" || targetDi?.item?.name === "ReportTable") {
              const tableRow = targetDi.children?.find(
                (c: IDisplayItem) => c.item?.name === "TableRow" || c.item?.name === "ReportTableRow"
              );
              if (tableRow?.id) {
                targetKuikaId = tableRow.id.toString();
              }
            }
          }

          // Auto-create Header/Footer if targeting -2 or -3 and it doesn't exist
          const ensureResult = ensureHeaderOrFooterExists(sd, targetKuikaId);
          if (ensureResult) {
            results.push({ error: ensureResult.error, componentName, targetKuikaId });
            continue;
          }

          // Auto-wrap: Row targeting a Stack → inject an invisible Panel wrapper, redirect Row into it.
          // Row cannot go into VerticalStack/HorizontalStack, but Panel can. By wrapping Row in a
          // Panel we preserve the real Row/Col grid with full responsive breakpoints (tablet/mobile).
          if (componentName === "Row") {
            const targetDi = dict[parseInt(targetKuikaId, 10)];
            const targetName = targetDi?.item?.name;
            if (targetName === "VerticalStack" || targetName === "HorizontalStack" || targetName === "Spacer") {
              // Inject an invisible Panel into the Stack as a wrapper for the Row
              sd.displayItemProvider.addChildItemByComponentName(targetKuikaId, "Panel");
              const parentAfterWrap = dict[parseInt(targetKuikaId, 10)];
              const wrapperPanel = parentAfterWrap?.children?.[parentAfterWrap.children.length - 1];
              if (wrapperPanel) {
                normalizeStyleObjTree(wrapperPanel);
                injectIntoDictionary(wrapperPanel, dict);
                const wrapperId = wrapperPanel.id?.toString();
                if (wrapperId) {
                  // Make the wrapper Panel invisible — no padding, no border, no background
                  if (!wrapperPanel.styleObj) wrapperPanel.styleObj = {};
                  if (!wrapperPanel.styleObj.position) {
                    wrapperPanel.styleObj.position = {
                      positionId: null,
                      positionIds: null,
                      kuikaid: wrapperId,
                      widthStyle: "px",
                      heightStyle: "px",
                      maxWidthStyle: "px",
                      maxHeightStyle: "px",
                      minWidthStyle: "px",
                      minHeightStyle: "px"
                    };
                  }
                  const position = wrapperPanel.styleObj.position;
                  position.paddingTop = 0;
                  position.paddingRight = 0;
                  position.paddingBottom = 0;
                  position.paddingLeft = 0;
                  // Redirect Row to target the wrapper Panel instead of the Stack
                  targetKuikaId = wrapperId;
                }
              }
            }
          }

          // Auto-wrap in Col when adding non-Col component directly into Row.
          // Row only accepts Col children — create a Col and add the component inside it.
          const targetDiForRowCheck = dict[parseInt(targetKuikaId, 10)];
          const isRowTarget = targetDiForRowCheck?.item?.name === "Row";
          const needsColWrap = isRowTarget && componentName !== "Col" && componentName !== "Row";

          if (needsColWrap) {
            try {
              const colWrapResult = addWithColWrap(sd, dict, targetKuikaId, componentName);
              if (!colWrapResult) {
                results.push({
                  error: "Failed to auto-wrap component in Col inside Row",
                  componentName,
                  targetKuikaId
                });
                continue;
              }

              let newChild = colWrapResult.actualDi;
              let newChildId = colWrapResult.actualId;
              applyPostCreationAdjustments(sd, newChild, colWrapResult.colDi.id?.toString());

              if (comp.insertBeforeKuikaId || comp.insertAfterKuikaId) {
                const reposition = repositionChild(
                  targetDiForRowCheck,
                  colWrapResult.colDi,
                  comp.insertBeforeKuikaId,
                  comp.insertAfterKuikaId
                );
                if (reposition.error) {
                  results.push({ error: reposition.error, componentName, targetKuikaId });
                  continue;
                }
              }

              if (friendlyName) {
                newChild.friendlyName = friendlyName;
              }
              applyContractChannels(newChild, comp);

              if (properties && Array.isArray(properties)) {
                for (const prop of properties) {
                  const { propertyName, value } = prop;
                  if (!propertyName || value === undefined) continue;
                  if (isDataEditorProperty(sdState, newChild, propertyName)) {
                    continue;
                  }
                  applyPropertyGridChangeWithoutCommit(
                    sdState,
                    { propertyName, value, kuikaId: newChildId },
                    ESdChangeReason.ChangePropertyValue
                  );
                }
              }

              applyComponentStylesWithStates(sd, newChild, newChildId, styles, stateStyles);
              applyComponentValidations(sd, newChild, validations);

              const newChildInfo = buildNewChildInfo(newChild);
              const hint = getComponentHint(componentName);

              // Register refs
              refMap.set(`$${compIdx}`, newChildId);
              if (comp.alias) refMap.set(`@${comp.alias}`, newChildId);

              results.push({
                success: true,
                kuikaId: newChildId,
                componentName,
                autoWrappedInCol: true,
                colKuikaId: colWrapResult.colDi.id?.toString(),
                newComponent: newChildInfo,
                ...(hint ? { keyProperties: hint } : {})
              });
              continue;
            } catch (err: any) {
              results.push({ error: `Col auto-wrap failed: ${err?.message || err}`, componentName, targetKuikaId });
              continue;
            }
          }

          // Validate drop rules
          const dropError = validateDrop(sd, targetKuikaId, componentName);
          if (dropError) {
            results.push({ error: dropError, componentName, targetKuikaId });
            continue;
          }

          const needsWrap = shouldAutoWrapInRowCol(sd, targetKuikaId, componentName);

          try {
            let newChild: any;
            let newChildId: string | null = null;
            let newChildInfo: any = null;

            if (needsWrap) {
              const wrapResult = addWithRowColWrap(sd, dict, targetKuikaId, componentName);
              if (!wrapResult) {
                results.push({ error: "Failed to add component with Row > Col wrapper" });
                continue;
              }
              newChild = wrapResult.actualDi;
              newChildId = wrapResult.actualId;
              applyPostCreationAdjustments(sd, newChild, wrapResult.colDi.id?.toString());

              // Reposition the Row wrapper
              if (comp.insertBeforeKuikaId || comp.insertAfterKuikaId) {
                const parentDi = dict[parseInt(targetKuikaId, 10)];
                const reposition = repositionChild(
                  parentDi,
                  wrapResult.rowDi,
                  comp.insertBeforeKuikaId,
                  comp.insertAfterKuikaId
                );
                if (reposition.error) {
                  results.push({ error: reposition.error, componentName, targetKuikaId });
                  continue;
                }
              }
            } else {
              const parentDi = dict[parseInt(targetKuikaId, 10)];

              // When adding a column to a TableRow that still has the auto-created
              // default columns (1 TableDefaultColumn + 1 TableTemplateColumn), remove
              // them first so only the LLM's explicit columns remain.
              // We bypass deleteItemByKuikaId because its parent-protection logic
              // (drag-drop-helper.ts:3026) cascades into deleting the TableRow when
              // only 1 child remains.
              if (
                COLUMN_COMPONENTS.has(componentName) &&
                parentDi?.children?.length === 2 &&
                (parentDi.item?.name === "TableRow" || parentDi.item?.name === "ReportTableRow")
              ) {
                const childTypes = parentDi.children.map((c: any) => c.item?.name);
                const isAutoCreatedDefaults =
                  childTypes.includes("TableDefaultColumn") && childTypes.includes("TableTemplateColumn");
                const isAutoCreatedReportDefaults =
                  childTypes.includes("ReportTableColumn") &&
                  childTypes.filter((t: string) => t === "ReportTableColumn").length === 2;
                if (isAutoCreatedDefaults || isAutoCreatedReportDefaults) {
                  const removeFromDict = (di: any) => {
                    if (di?.id != null) delete dict[di.id];
                    if (di?.children) {
                      for (const child of di.children) removeFromDict(child);
                    }
                  };
                  for (const colDi of parentDi.children) {
                    removeFromDict(colDi);
                  }
                  parentDi.children.length = 0;
                }
              }

              const childrenBefore = parentDi?.children?.length || 0;

              sd.displayItemProvider.addChildItemByComponentName(targetKuikaId, componentName);

              const childrenAfter = parentDi?.children || [];
              if (childrenAfter.length <= childrenBefore) {
                results.push({ error: "Component was not added (unknown reason)" });
                continue;
              }

              newChild = childrenAfter[childrenAfter.length - 1];
              newChildId = newChild.id?.toString();

              normalizeStyleObjTree(newChild);
              injectIntoDictionary(newChild, dict);
              applyPostCreationAdjustments(sd, newChild, targetKuikaId);

              const reposition = repositionChild(parentDi, newChild, comp.insertBeforeKuikaId, comp.insertAfterKuikaId);
              if (reposition.error) {
                results.push({ error: reposition.error, componentName, targetKuikaId });
                continue;
              }
            }

            if (newChild && newChildId) {
              if (friendlyName) {
                newChild.friendlyName = friendlyName;
              }
              applyContractChannels(newChild, comp);

              if (newChild.item?.name === "Row" && !needsWrap) {
                applyRowColumns(
                  sd,
                  newChild,
                  newChildId,
                  comp.columns ?? comp.columnCount,
                  comp.tabletColumns,
                  comp.mobileColumns
                );
                // Re-inject into dictionary after column changes — setColumnsNumber may
                // create new Col display items that aren't in the dict yet
                injectIntoDictionary(newChild, dict);

                // Apply Col styles inline (align, valign, etc.).
                // Col styles are attached to the Row's add entry to avoid $N.M refMap
                // collisions when the agent-loop sorts add calls before style calls.
                if (comp.colStyles && Array.isArray(comp.colStyles) && newChild.children) {
                  for (const cs of comp.colStyles) {
                    const colDi = newChild.children[cs.colIndex];
                    if (!colDi) continue;
                    const colKuikaId = colDi.id?.toString();
                    if (!colKuikaId) continue;
                    applyComponentStylesWithStates(
                      sd,
                      colDi,
                      colKuikaId,
                      cs.styles,
                      (cs.stateStyles as StateStylesInput) ?? undefined
                    );
                  }
                }
              }

              if (properties && Array.isArray(properties)) {
                for (const prop of properties) {
                  const { propertyName, value } = prop;
                  if (!propertyName || value === undefined) continue;
                  if (isDataEditorProperty(sdState, newChild, propertyName)) {
                    continue;
                  }
                  applyPropertyGridChangeWithoutCommit(
                    sdState,
                    { propertyName, value, kuikaId: newChildId },
                    ESdChangeReason.ChangePropertyValue
                  );
                }
              }

              // Apply custom styles + state styles if provided
              applyComponentStylesWithStates(sd, newChild, newChildId, styles, stateStyles);
              applyComponentValidations(sd, newChild, validations);

              // Structural-parent extra-child friendlyName alignment.
              // When the LLM emits more structural children than a parent
              // auto-creates (Collapse defaultCount=2 with 3 panels in
              // markup), the 3rd panel goes through the manual addChild
              // path and gets a global occurrence-counted name like
              // "CollapsePanel3", while siblings born via the auto-create
              // loop in screen-design-helper.ts get parent-prefixed names
              // like "CollapsePanel1_0" / "CollapsePanel1_1". The tree
              // pane then looks like the third panel doesn't belong to
              // the same Collapse. Match the auto-create naming by
              // taking the parent prefix from a sibling and appending
              // `_<structuralChildIndex>`. Skip silently if there's no
              // sibling with the expected pattern (defensive fallback —
              // the existing global name still works, it just looks odd).
              if (typeof comp.structuralChildIndex === "number" && newChild) {
                const parentDi = newChild.parentId != null ? dict[newChild.parentId] : undefined;
                if (parentDi?.children) {
                  for (const sibling of parentDi.children) {
                    if (sibling === newChild) continue;
                    if (sibling.item?.name !== newChild.item?.name) continue;
                    const siblingName = sibling.friendlyName;
                    if (typeof siblingName !== "string") continue;
                    const match = siblingName.match(/^(.+?)_\d+$/);
                    if (match) {
                      newChild.friendlyName = `${match[1]}_${comp.structuralChildIndex}`;
                      break;
                    }
                  }
                }
              }

              newChildInfo = buildNewChildInfo(newChild);
            }

            const hint = getComponentHint(componentName);

            // Register placeholder ref so subsequent items can target this component
            if (newChildInfo?.kuikaId) {
              refMap.set(`$${compIdx}`, newChildInfo.kuikaId);

              // Register @alias if the component declared one
              const alias = comp.alias?.trim();
              if (alias) {
                refMap.set(`@${alias}`, newChildInfo.kuikaId);
              }

              // Also register auto-generated children so they can be targeted via $N.M refs
              // e.g., Row at $5 with 4 Cols → $5.0, $5.1, $5.2, $5.3
              if (newChildInfo.children && newChildInfo.children.length > 0) {
                for (let childIdx = 0; childIdx < newChildInfo.children.length; childIdx++) {
                  const childKuikaId = newChildInfo.children[childIdx].kuikaId;
                  if (childKuikaId) {
                    refMap.set(`$${compIdx}.${childIdx}`, childKuikaId);
                  }
                }
              }

              // Apply childConfigs: properties/styles for auto-created children
              // (e.g., TabItem labels, StepperItem titles). Applied inline during add
              // so they don't depend on separate configure calls with ref resolution.
              if (comp.childConfigs && Array.isArray(comp.childConfigs) && newChild?.children) {
                for (const cc of comp.childConfigs) {
                  const childDi = newChild.children[cc.childIndex];
                  if (!childDi) continue;
                  const childKuikaId = childDi.id?.toString();
                  if (!childKuikaId) continue;
                  if (cc.properties && Array.isArray(cc.properties)) {
                    for (const prop of cc.properties) {
                      if (!prop.propertyName || prop.value === undefined) continue;
                      applyPropertyGridChangeWithoutCommit(
                        sdState,
                        { propertyName: prop.propertyName, value: prop.value, kuikaId: childKuikaId },
                        ESdChangeReason.ChangePropertyValue
                      );
                    }
                  }
                  applyComponentStylesWithStates(
                    sd,
                    childDi,
                    childKuikaId,
                    cc.styles,
                    (cc.stateStyles as StateStylesInput) ?? undefined
                  );
                }
              }

              // Table auto-column cleanup: when explicit columns are being added
              // alongside this Table in the same batch, remove the auto-created
              // DefaultColumn + TemplateColumn so only the LLM's columns remain.
              // We detect this by scanning ahead in the components array for any
              // column component targeting this Table (via $compIdx ref).
              // We bypass deleteItemByKuikaId because its parent-protection logic
              // (drag-drop-helper.ts:3026) cascades into deleting the TableRow when
              // only 1 child remains, instead of removing the last column.
              if ((componentName === "Table" || componentName === "ReportTable") && newChildInfo.children) {
                const tableRef = `$${compIdx}`;
                const hasExplicitColumns =
                  comp.deleteAutoColumns ||
                  components
                    .slice(compIdx + 1)
                    .some((c: any) => COLUMN_COMPONENTS.has(c.componentName) && c.targetKuikaId === tableRef);
                if (hasExplicitColumns) {
                  // Find the TableRow child (usually at index 1, after TableHeader)
                  const tableRowChild = newChildInfo.children.find(
                    (c: { componentType?: string }) =>
                      c.componentType === "TableRow" || c.componentType === "ReportTableRow"
                  );
                  if (tableRowChild?.kuikaId) {
                    const tableRowDi = dict[parseInt(tableRowChild.kuikaId, 10)];
                    if (tableRowDi?.children && tableRowDi.children.length > 0) {
                      // Remove all auto-created columns directly from the children array
                      // and clean up their dictionary entries (including grandchildren).
                      const removeFromDict = (di: any) => {
                        if (di?.id != null) delete dict[di.id];
                        if (di?.children) {
                          for (const child of di.children) removeFromDict(child);
                        }
                      };
                      for (const colDi of tableRowDi.children) {
                        removeFromDict(colDi);
                      }
                      tableRowDi.children.length = 0;
                    }
                  }
                }
              }

              // Structural-parent leftover-child trim: when the LLM emitted
              // FEWER structural children than the runtime auto-created
              // (e.g. <Collapse><CollapsePanel header="FAQ"/></Collapse> on
              // a parent that defaults to 2 panels), remove the trailing
              // auto-created defaults so the LLM's intended count survives.
              // Mirrors the Table auto-column cleanup above; bypasses
              // deleteItemByKuikaId for the same parent-protection reason.
              const trimTo =
                typeof comp.trimAutoChildrenTo === "number" && comp.trimAutoChildrenTo >= 0
                  ? comp.trimAutoChildrenTo
                  : undefined;
              if (trimTo !== undefined && newChild?.children && newChild.children.length > trimTo) {
                const removeFromDict = (di: any) => {
                  if (di?.id != null) delete dict[di.id];
                  if (di?.children) {
                    for (const child of di.children) removeFromDict(child);
                  }
                };
                for (let i = trimTo; i < newChild.children.length; i++) {
                  removeFromDict(newChild.children[i]);
                }
                newChild.children.length = trimTo;
                // Refresh newChildInfo so the response reflects the trimmed shape.
                newChildInfo = buildNewChildInfo(newChild);
              }
            }

            const warnings = getCreationWarnings(componentName, styles, comp);

            results.push({
              success: true,
              newComponent: newChildInfo,
              ...(hint ? { hint } : {}),
              ...(warnings.length > 0 ? { warnings } : {})
            });
          } catch (e: any) {
            results.push({ error: e.message, componentName, targetKuikaId });
          }
        }
      } finally {
        // Always restore setScreenDesign, even if the loop throws
        sd.screenProvider.setScreenDesign = realSetScreenDesign;
      }
      const sd2 = getSdInstance();
      const screenDesign = sd2.screenProvider.getScreenDesign();
      sd2.screenProvider.setScreenDesign(screenDesign, ESdChangeReason.ChangeStyleValue);

      // Second pass: re-read children for TableTemplateColumn/TableDefaultColumn created in this batch.
      // When a column and its child Label are added in the same batch via @alias, the Label is added
      // AFTER the column in the loop — so the column's children array was empty at build time.
      const sd3 = getSdInstance();
      const freshDict = sd3.screenProvider.state.screenDesignDictionary;
      for (const result of results) {
        if (!result.success || !result.newComponent?.kuikaId) continue;
        const compType = result.newComponent.componentType;
        if (compType !== "TableTemplateColumn" && compType !== "TableDefaultColumn") continue;

        const di = freshDict[parseInt(result.newComponent.kuikaId, 10)];
        if (di?.children?.length > 0 && (!result.newComponent.children || result.newComponent.children.length === 0)) {
          result.newComponent.children = di.children.map((ch: any) => ({
            kuikaId: ch.id?.toString(),
            name: ch.item?.name,
            componentType: ch.item?.name,
            friendlyName: ch.friendlyName || null
          }));
          result.newComponent.childCount = di.children.length;
        }
      }

      // Store refMap for subsequent configure calls to resolve $N.M and @alias refs.
      // MERGE into existing maps so refs from earlier add chunks survive.
      // Each add chunk re-indexes to $0, $1, … so bare $N keys from earlier chunks
      // get overwritten, but $N.M keys (auto-children like Tab→TabItem) are unique
      // per chunk and must persist for the configure call that runs after all chunks.
      (window as any).kuika = (window as any).kuika || {};
      const existingRefMap: Record<string, string> = (window as any).kuika._mcpLastRefMap || {};
      const existingAliasMap: Record<string, string> = (window as any).kuika._mcpLastAliasMap || {};
      for (const [key, value] of refMap.entries()) {
        if (key.startsWith("@")) {
          existingAliasMap[key.slice(1)] = value;
        } else {
          existingRefMap[key] = value;
        }
      }
      (window as any).kuika._mcpLastRefMap = existingRefMap;
      (window as any).kuika._mcpLastAliasMap = existingAliasMap;

      // Auto-select the last successfully added component
      const lastSuccess = [...results].reverse().find((r) => r.success && r.newComponent?.kuikaId);
      if (lastSuccess) {
        sd.displayItemProvider.setSelectedDisplayItem(lastSuccess.newComponent.kuikaId);
      }

      return { ...summarizeBatchResults(results), results };
    },

    ui_structure_configure_components: (input) => {
      const components = safeParse(input.components);
      if (!components || !Array.isArray(components) || components.length === 0) {
        return { error: "components array is required" };
      }

      const sd = getSdInstance();
      const sdState = getValues(sd);
      const dict = sd.screenProvider.state.screenDesignDictionary;
      // Resolve @alias refs using the latest add response's ids map (stored on window by add handler)
      const aliasMap: Record<string, string> = (window as any).kuika?._mcpLastAliasMap || {};
      const results: any[] = [];

      for (const comp of components) {
        let { kuikaId, friendlyName } = comp;
        const properties = safeParse(comp.properties);
        const styles = safeParse(comp.styles);
        const stateStyles = (comp.stateStyles as StateStylesInput) ?? undefined;
        if (!kuikaId) {
          results.push({ error: "kuikaId is required", input: comp });
          continue;
        }

        // Resolve @alias references (e.g., "@_c5" → actual kuikaId)
        // Supports @alias.N notation for auto-created children (e.g., "@_tab.0" → first TabItem)
        if (typeof kuikaId === "string" && kuikaId.startsWith("@")) {
          const aliasDotMatch = kuikaId.match(/^@([^.]+)\.(\d+)$/);
          if (aliasDotMatch) {
            const parentAlias = aliasDotMatch[1];
            const childIdx = parseInt(aliasDotMatch[2], 10);
            const parentId = aliasMap[parentAlias];
            if (parentId) {
              const parentDi = dict[parseInt(parentId, 10)];
              if (parentDi?.children && parentDi.children[childIdx]) {
                kuikaId = parentDi.children[childIdx].id?.toString();
              }
            }
          } else {
            const aliasName = kuikaId.slice(1);
            const resolved = aliasMap[aliasName];
            if (resolved) {
              kuikaId = resolved;
            }
          }
        }

        // Resolve $N.M refs (auto-child references) by looking up the component tree
        if (typeof kuikaId === "string" && kuikaId.startsWith("$")) {
          const refMap: Record<string, string> = (window as any).kuika?._mcpLastRefMap || {};
          const resolved = refMap[kuikaId];
          if (resolved) {
            kuikaId = resolved;
          }
        }

        try {
          const di = dict[parseInt(kuikaId, 10)];

          if (!di || !di.item) {
            results.push({ success: false, error: `Component with kuikaId ${kuikaId} not found`, kuikaId });
            continue;
          }

          // KOA-17394 (Task #17) — friendlyName-conflict guard. The
          // 13:51 PM fikir-havuzu rerun had Designer's recovery render
          // emit `<Button kuikaId="209142" id="btn-new-idea">`, which
          // tried to reassign kuikaId 209142's friendlyName to a value
          // already used by another component. Two components with
          // the same friendlyName break downstream find_by_label /
          // componentValue lookups.
          //
          // (The server-side render-expander also catches this earlier
          // via findBraidIdMutationConflict — see runtime/render-
          // expander.ts. This handler-level check is defense-in-depth
          // for non-render code paths that set friendlyName directly.)
          if (friendlyName && typeof friendlyName === "string" && friendlyName.length > 0) {
            const conflicts: string[] = [];
            for (const otherKey of Object.keys(dict)) {
              if (otherKey === String(kuikaId)) continue;
              const other: any = dict[parseInt(otherKey, 10)];
              if (other?.friendlyName === friendlyName) {
                conflicts.push(otherKey);
              }
            }
            if (conflicts.length > 0) {
              results.push({
                success: false,
                kuikaId,
                error:
                  `friendlyName conflict: "${friendlyName}" is already assigned to kuikaId ` +
                  `${conflicts.join(", ")}. A friendlyName is a UNIQUE per-screen identifier — the Wirer ` +
                  `and Binder use it to look up components by name (find_component_by_label, ` +
                  `componentValue.<friendlyName>). Re-assigning it to kuikaId ${kuikaId} would create ` +
                  `TWO components with the same name and downstream lookups become ambiguous. If you ` +
                  `meant to ADD a NEW component, OMIT the kuikaId so the runtime allocates a fresh ` +
                  `one. If you meant to RENAME this component, first ui_structure_delete_component ` +
                  `the existing one at kuikaId ${conflicts.join(", ")}, then retry.`
              });
              continue;
            }
          }

          if (friendlyName) {
            di.friendlyName = friendlyName;
          }
          applyContractChannels(di, comp);

          if (properties && Array.isArray(properties)) {
            for (const prop of properties) {
              const { propertyName, value } = prop;
              if (!propertyName || value === undefined) continue;
              if (isDataEditorProperty(sdState, di, propertyName)) {
                continue;
              }
              applyPropertyGridChangeWithoutCommit(
                sdState,
                { propertyName, value, kuikaId },
                ESdChangeReason.ChangePropertyValue
              );
            }
          }

          // Apply custom styles + state styles if provided (skip structural wrappers
          // unless the style payload is whitelisted for wrappers).
          const allowOnWrapper = styles && canApplyStylesToStructuralWrapper(styles as Record<string, unknown>);
          if (!isStructuralWrapper(di, dict) || allowOnWrapper) {
            applyComponentStylesWithStates(sd, di, kuikaId, styles, stateStyles);
          }

          results.push({ success: true, kuikaId });
        } catch (e: any) {
          results.push({ error: e.message, kuikaId });
        }
      }

      // Commit friendlyName changes in one go
      const sd2 = getSdInstance();
      const screenDesign = sd2.screenProvider.getScreenDesign();
      sd2.screenProvider.setScreenDesign(screenDesign, 1);

      return { ...summarizeBatchResults(results), results };
    },

    ui_structure_delete_component: (input) => {
      const kuikaIds = safeParse(input.kuikaIds);

      // Batch mode
      if (kuikaIds && Array.isArray(kuikaIds) && kuikaIds.length > 0) {
        if (kuikaIds.length > 50) {
          return { error: "Maximum 50 components per batch delete" };
        }

        const sd = getSdInstance();
        const deletedSet = new Set<string>();
        const releasedActionIds = new Set<string>();
        const results: any[] = [];

        for (const rawId of kuikaIds) {
          const id = String(rawId);
          try {
            results.push(deleteSingleComponent(sd, id, deletedSet, releasedActionIds));
          } catch (e: any) {
            results.push({ success: false, error: e.message, kuikaId: id });
          }
        }

        const successCount = results.filter((r) => r.success).length;
        const skippedCount = results.filter((r) => r.skipped).length;
        const failedCount = results.filter((r) => r.success === false).length;
        const sweep = sweepOrphanedScreenActions(sd, releasedActionIds);

        return {
          success: failedCount === 0,
          successCount,
          skippedCount,
          failedCount,
          ...(sweep.removedActionIds.length > 0 ? { removedScreenActionIds: sweep.removedActionIds } : {}),
          ...(failedCount > 0
            ? {
                error: results
                  .filter((r) => r.success === false)
                  .slice(0, 3)
                  .map((r) => r.error)
                  .join("; ")
              }
            : {}),
          results
        };
      }

      // Single mode (existing behavior)
      const { kuikaId } = input;
      if (!kuikaId) return { error: "kuikaId is required (or use kuikaIds[] for batch)" };

      const sd = getSdInstance();
      const deletedSet = new Set<string>();
      const releasedActionIds = new Set<string>();
      const result = deleteSingleComponent(sd, String(kuikaId), deletedSet, releasedActionIds);
      const sweep = sweepOrphanedScreenActions(sd, releasedActionIds);
      return {
        ...result,
        ...(sweep.removedActionIds.length > 0 ? { removedScreenActionIds: sweep.removedActionIds } : {})
      };
    },

    ui_structure_select_component: (input) => {
      const { kuikaId } = input;
      if (!kuikaId) return { error: "kuikaId is required" };

      const sd = getSdInstance();
      const dict = sd.screenProvider.state.screenDesignDictionary;
      const di = dict[parseInt(kuikaId, 10)];
      if (!di || !di.item) {
        return { success: false, error: `Component with kuikaId ${kuikaId} not found` };
      }

      sd.displayItemProvider.setSelectedDisplayItem(kuikaId);
      return {
        success: true,
        kuikaId: String(kuikaId),
        componentType: di.item.name || "unknown",
        alias: di.friendlyName || di.title || undefined
      };
    },

    ui_structure_move_component: (input) => {
      const { kuikaId, targetParentKuikaId, insertBefore, insertAfter } = input;
      if (!kuikaId || !targetParentKuikaId) {
        return { error: "kuikaId and targetParentKuikaId are required" };
      }

      const srcId = String(kuikaId);
      if (srcId === "-1" || srcId === "-2" || srcId === "-3") {
        return { success: false, error: "Cannot move root section (Body/Header/Footer)" };
      }

      if (kuikaId === targetParentKuikaId) {
        return { error: "Cannot move a component into itself" };
      }

      // Positional move (the p2-4 move contract, implemented for the Plan B
      // redesign reconcile): place the moved node relative to an existing sibling in the
      // target parent instead of appending. Mutually exclusive; validated upfront
      // before any mutation.
      if (insertBefore && insertAfter) {
        return { error: "insertBefore and insertAfter are mutually exclusive" };
      }

      const sd = getSdInstance();
      const dict = sd.screenProvider.state.screenDesignDictionary;
      const screenDesign = sd.screenProvider.getScreenDesign();

      const sourceId = parseInt(kuikaId, 10);
      const targetId = parseInt(targetParentKuikaId, 10);
      const sourceDi = dict[sourceId];
      const targetDi = dict[targetId];

      if (!sourceDi) return { error: `Source component "${kuikaId}" not found` };
      if (!targetDi) return { error: `Target parent "${targetParentKuikaId}" not found` };

      // Prevent circular move: target must not be a descendant of source
      let ancestor: typeof targetDi | null = targetDi;
      while (ancestor) {
        if (ancestor.id === sourceId) {
          return { error: "Cannot move a component into its own descendant (circular)" };
        }
        ancestor = ancestor.parentId != null ? dict[ancestor.parentId] : null;
      }

      // Auto-wrap in Col when moving a non-Col component into a Row
      const sourceCompName = sourceDi.item?.name;
      const targetCompName = targetDi.item?.name;
      let effectiveTargetId = targetId;
      let effectiveTargetDi = targetDi;
      let autoCreatedColId: string | null = null;

      if (targetCompName === "Row" && sourceCompName !== "Col" && sourceCompName !== "Row") {
        // Create a Col in the Row, then move the component into the Col
        const rowChildrenBefore = targetDi.children?.length || 0;
        sd.displayItemProvider.addChildItemByComponentName(targetParentKuikaId, "Col");
        const rowChildrenAfter = targetDi.children || [];
        if (rowChildrenAfter.length <= rowChildrenBefore) {
          return { error: "Failed to auto-create Col wrapper in Row for move" };
        }
        const colDi = rowChildrenAfter[rowChildrenAfter.length - 1];
        normalizeStyleObjTree(colDi);
        injectIntoDictionary(colDi, dict);
        effectiveTargetId = colDi.id;
        effectiveTargetDi = colDi;
        autoCreatedColId = colDi.id?.toString() ?? null;
      } else {
        // Validate drop rules (skip for auto-wrap — Col always accepts children)
        const dropError = validateDrop(sd, targetParentKuikaId, sourceCompName ?? "");
        if (dropError) return { error: dropError };
      }

      // Already in the target parent — no-op
      if (sourceDi.parentId === effectiveTargetId) {
        return {
          success: true,
          noOp: true,
          kuikaId,
          componentType: sourceCompName || "unknown",
          alias: sourceDi.friendlyName || sourceDi.title || undefined,
          message: "Component is already in the target parent"
        };
      }

      // --- Step 1: Remove source from old parent ---
      const oldParentId = sourceDi.parentId;
      let oldParentDi: any = null;
      if (oldParentId != null) {
        oldParentDi = dict[oldParentId];
      }

      let removedFromOldParent = false;
      if (oldParentDi && oldParentDi.children) {
        const idx = oldParentDi.children.indexOf(sourceDi);
        if (idx !== -1) {
          oldParentDi.children.splice(idx, 1);
          removedFromOldParent = true;
        }
      }
      if (!removedFromOldParent) {
        // Fallback: search the tree for the source and remove it
        const treeParent = ScreenDesignHelper.findDisplayItemParentByKuikaId(kuikaId, screenDesign.displayItems || []);
        if (treeParent && treeParent.children) {
          const idx = treeParent.children.findIndex((c: any) => c.id?.toString() === kuikaId);
          if (idx !== -1) {
            treeParent.children.splice(idx, 1);
            oldParentDi = treeParent;
            removedFromOldParent = true;
          }
        }
      }
      if (!removedFromOldParent) {
        return { error: `Could not remove component "${kuikaId}" from its current parent` };
      }

      // --- Step 2: Add source to new parent (Col if auto-wrapped, original target otherwise) ---
      sourceDi.parentId = effectiveTargetId;
      if (!effectiveTargetDi.children) effectiveTargetDi.children = [];
      effectiveTargetDi.children.push(sourceDi);

      // --- Step 2b: Position relative to an existing sibling (best-effort) ---
      // The reparent above is the load-bearing move (kuikaId + all wiring intact);
      // an absent/ambiguous anchor leaves the node appended rather than failing the
      // whole move. Reuses the same repositionChild the add path uses.
      let positionWarning: string | undefined;
      let repositioned = false;
      if (insertBefore || insertAfter) {
        const reposition = repositionChild(
          effectiveTargetDi,
          sourceDi,
          insertBefore ? String(insertBefore) : undefined,
          insertAfter ? String(insertAfter) : undefined
        );
        repositioned = reposition.repositioned;
        if (reposition.error) positionWarning = reposition.error;
      }

      // --- Step 3: Clean up empty Row/Col wrappers left behind ---
      cleanupEmptyRowColWrappers(oldParentDi, dict, screenDesign);

      // --- Step 4: Persist ---
      sd.screenProvider.setScreenDesign(screenDesign, 1, kuikaId);

      // --- Step 5: Verify ---
      const isInTarget = effectiveTargetDi.children.some((c: any) => c.id?.toString() === kuikaId);
      if (!isInTarget) {
        return { error: "Move verification failed — component not found in target after move" };
      }

      return {
        success: true,
        kuikaId,
        componentType: sourceCompName || "unknown",
        alias: sourceDi.friendlyName || sourceDi.title || undefined,
        targetParentKuikaId: String(effectiveTargetId),
        ...(repositioned ? { repositioned: true } : {}),
        ...(positionWarning ? { positionWarning } : {}),
        ...(autoCreatedColId ? { autoWrappedInCol: true, colKuikaId: autoCreatedColId } : {})
      };
    },

    ui_structure_clone_component: (input) => {
      const { kuikaId, targetParentKuikaId } = input;
      if (!kuikaId) return { error: "kuikaId is required" };

      const sd = getSdInstance();
      const dict = sd.screenProvider.state.screenDesignDictionary;
      const screenDesign = sd.screenProvider.getScreenDesign();
      const di = dict?.[parseInt(kuikaId, 10)];
      if (!di) return { error: `Component "${kuikaId}" not found` };

      // --- Pre-validate cross-parent target before cloning ---
      const targetId = targetParentKuikaId ? parseInt(targetParentKuikaId, 10) : null;
      let targetDi: any = null;
      let cloneAutoColId: string | null = null;
      if (targetId != null) {
        targetDi = dict[targetId];
        if (!targetDi) return { error: `Target parent "${targetParentKuikaId}" not found` };

        // Auto-wrap in Col when cloning into Row
        const targetCompName = targetDi.item?.name;
        const sourceCompName = di.item?.name || "";
        if (targetCompName === "Row" && sourceCompName !== "Col" && sourceCompName !== "Row") {
          // Create a Col in the Row to receive the clone
          const rowChildrenBefore = targetDi.children?.length || 0;
          sd.displayItemProvider.addChildItemByComponentName(String(targetParentKuikaId), "Col");
          const rowChildrenAfter = targetDi.children || [];
          if (rowChildrenAfter.length <= rowChildrenBefore) {
            return { error: "Failed to auto-create Col wrapper in Row for clone" };
          }
          const colDi = rowChildrenAfter[rowChildrenAfter.length - 1];
          normalizeStyleObjTree(colDi);
          injectIntoDictionary(colDi, dict);
          cloneAutoColId = colDi.id?.toString() || null;
          // Redirect targetDi/targetId to the new Col
          targetDi = colDi;
        } else {
          const dropError = validateDrop(sd, String(targetParentKuikaId), sourceCompName);
          if (dropError) return { error: dropError };
        }
      }

      // Find parent children array to detect the clone
      let parentChildren: any[] | null = null;
      let cloneParentDi: any = null;
      if (di.parentId != null) {
        cloneParentDi = dict[di.parentId];
        parentChildren = cloneParentDi?.children || null;
      } else {
        parentChildren = screenDesign?.displayItems || null;
      }

      const originalIndex = parentChildren ? parentChildren.indexOf(di) : -1;
      const childCountBefore = parentChildren ? parentChildren.length : 0;

      sd.displayItemProvider.cloneDI(kuikaId);

      // Try to find the cloned component
      const sourceComponentType = di.item?.name || "unknown";
      const sourceAlias = di.friendlyName || di.title || undefined;

      if (!parentChildren || parentChildren.length <= childCountBefore || originalIndex < 0) {
        return {
          success: true,
          sourceKuikaId: kuikaId,
          componentType: sourceComponentType,
          alias: sourceAlias,
          message: `Cloned ${sourceComponentType}. Could not determine new kuikaId — use get_tree to find it.`
        };
      }

      const clonedDi = parentChildren[originalIndex + 1];
      if (!clonedDi) {
        return {
          success: true,
          sourceKuikaId: kuikaId,
          componentType: sourceComponentType,
          alias: sourceAlias,
          message: `Cloned ${sourceComponentType}. Could not determine new kuikaId — use get_tree to find it.`
        };
      }

      const newKuikaId = clonedDi.id?.toString();

      // --- Cross-parent: move clone to target (may be auto-created Col) ---
      const effectiveTargetId = targetDi?.id ?? targetId;
      const needsMove = effectiveTargetId != null && targetDi && di.parentId !== effectiveTargetId;
      if (needsMove) {
        // Remove clone from current parent
        const cloneIdx = parentChildren!.indexOf(clonedDi);
        if (cloneIdx !== -1) {
          parentChildren!.splice(cloneIdx, 1);
        }

        // Reparent into target (Col if auto-wrapped, original target otherwise)
        clonedDi.parentId = effectiveTargetId;
        if (!targetDi.children) targetDi.children = [];
        targetDi.children.push(clonedDi);

        // Clean up empty wrappers left behind
        cleanupEmptyRowColWrappers(cloneParentDi, dict, screenDesign);

        // Persist
        sd.screenProvider.setScreenDesign(screenDesign, 1, newKuikaId);
      }

      return {
        success: true,
        sourceKuikaId: kuikaId,
        newKuikaId,
        componentType: sourceComponentType,
        alias: sourceAlias,
        ...(cloneAutoColId ? { autoWrappedInCol: true, colKuikaId: cloneAutoColId } : {})
      };
    },

    ui_structure_reorder_component: (input) => {
      const { kuikaId, direction, beforeKuikaId, afterKuikaId } = input;
      const index = input.index != null ? Number(input.index) : undefined;
      if (!kuikaId) return { error: "kuikaId is required" };

      // Validate exactly one positioning param
      const posParams = [direction, beforeKuikaId, afterKuikaId, index !== undefined ? index : undefined].filter(
        (v) => v != null
      );
      if (posParams.length === 0) {
        return { error: "Provide one of: direction, beforeKuikaId, afterKuikaId, or index" };
      }
      if (posParams.length > 1) {
        return {
          error: "Only one positioning param allowed at a time (direction, beforeKuikaId, afterKuikaId, or index)"
        };
      }

      const sd = getSdInstance();
      const dict = sd.screenProvider.state.screenDesignDictionary;
      const di = dict[parseInt(kuikaId, 10)];
      if (!di) return { error: `Component "${kuikaId}" not found` };

      const parentDi = di.parentId != null ? dict[di.parentId] : null;
      if (!parentDi || !parentDi.children) {
        return { error: `Cannot reorder: parent not found for component "${kuikaId}"` };
      }

      const siblings = parentDi.children;
      const currentIdx = siblings.indexOf(di);
      if (currentIdx === -1) {
        return { error: `Component "${kuikaId}" not found in parent's children` };
      }

      const compType = di.item?.name || "unknown";
      const compAlias = di.friendlyName || di.title || undefined;
      const reorderResult = (newIndex: number, noOp?: boolean) => ({
        success: true,
        kuikaId,
        componentType: compType,
        alias: compAlias,
        position: { old: currentIdx, new: newIndex, totalSiblings: siblings.length },
        ...(noOp ? { noOp: true } : {})
      });

      let targetIdx: number;

      if (direction) {
        switch (direction) {
          case "up":
            targetIdx = currentIdx - 1;
            break;
          case "down":
            targetIdx = currentIdx + 1;
            break;
          case "first":
            targetIdx = 0;
            break;
          case "last":
            targetIdx = siblings.length - 1;
            break;
          default:
            return { error: `Invalid direction: "${direction}". Use "up", "down", "first", or "last"` };
        }
      } else if (beforeKuikaId) {
        const targetDi = siblings.find((s: any) => s.id?.toString() === beforeKuikaId);
        if (!targetDi) return { error: `Sibling "${beforeKuikaId}" not found in same parent` };
        // Remove first, then find target in modified array
        siblings.splice(currentIdx, 1);
        targetIdx = siblings.indexOf(targetDi);
        // Insert at target position
        siblings.splice(targetIdx, 0, di);
        const screenDesign = sd.screenProvider.getScreenDesign();
        sd.screenProvider.setScreenDesign(screenDesign, 1, kuikaId);
        return reorderResult(targetIdx);
      } else if (afterKuikaId) {
        const targetDi = siblings.find((s: any) => s.id?.toString() === afterKuikaId);
        if (!targetDi) return { error: `Sibling "${afterKuikaId}" not found in same parent` };
        siblings.splice(currentIdx, 1);
        targetIdx = siblings.indexOf(targetDi) + 1;
        siblings.splice(targetIdx, 0, di);
        const screenDesign = sd.screenProvider.getScreenDesign();
        sd.screenProvider.setScreenDesign(screenDesign, 1, kuikaId);
        return reorderResult(targetIdx);
      } else if (index !== undefined) {
        targetIdx = Math.max(0, Math.min(index, siblings.length - 1));
      } else {
        return { error: "No positioning param provided" };
      }

      // Boundary check for direction / index
      if (targetIdx < 0 || targetIdx >= siblings.length || targetIdx === currentIdx) {
        return reorderResult(currentIdx, true);
      }

      // Remove from current position, insert at target
      siblings.splice(currentIdx, 1);
      siblings.splice(targetIdx, 0, di);

      const screenDesign = sd.screenProvider.getScreenDesign();
      sd.screenProvider.setScreenDesign(screenDesign, 1, kuikaId);

      return reorderResult(targetIdx);
    },

    ui_structure_swap_components: (input) => {
      const { firstKuikaId, secondKuikaId } = input;
      if (!firstKuikaId || !secondKuikaId) return { error: "firstKuikaId and secondKuikaId are required" };
      if (firstKuikaId === secondKuikaId) return { error: "Cannot swap a component with itself" };

      const sd = getSdInstance();
      const dict = sd.screenProvider.state.screenDesignDictionary;
      const firstId = parseInt(firstKuikaId, 10);
      const secondId = parseInt(secondKuikaId, 10);

      const diA = dict[firstId];
      const diB = dict[secondId];
      if (!diA) return { error: `Component "${firstKuikaId}" not found` };
      if (!diB) return { error: `Component "${secondKuikaId}" not found` };

      const parentIdA = diA.parentId;
      const parentIdB = diB.parentId;
      if (parentIdA == null || parentIdB == null) {
        return { error: "Cannot swap root-level sections (Header/Body/Footer)" };
      }
      if (parentIdA !== parentIdB) {
        return {
          error: `Components must share the same parent. "${firstKuikaId}" is in parent ${parentIdA}, "${secondKuikaId}" is in parent ${parentIdB}`
        };
      }

      const parentDi = dict[parentIdA];
      if (!parentDi || !parentDi.children) {
        return { error: `Parent container ${parentIdA} not found or has no children` };
      }

      const siblings = parentDi.children;
      const idxA = siblings.indexOf(diA);
      const idxB = siblings.indexOf(diB);
      if (idxA === -1 || idxB === -1) {
        return { error: "Component not found in parent's children array" };
      }

      siblings[idxA] = diB;
      siblings[idxB] = diA;

      const screenDesign = sd.screenProvider.getScreenDesign();
      sd.screenProvider.setScreenDesign(screenDesign, 1, firstKuikaId);

      return {
        success: true,
        swapped: [
          {
            kuikaId: firstKuikaId,
            componentType: diA.item?.name || "unknown",
            alias: diA.friendlyName || diA.title || undefined
          },
          {
            kuikaId: secondKuikaId,
            componentType: diB.item?.name || "unknown",
            alias: diB.friendlyName || diB.title || undefined
          }
        ]
      };
    },

    ui_structure_rename_component: (input) => {
      const { kuikaId, newName } = input;
      if (!kuikaId || !newName) return { error: "kuikaId and newName are required" };

      const sd = getSdInstance();
      const dict = sd.screenProvider.state.screenDesignDictionary;
      const di = dict[parseInt(kuikaId, 10)];
      if (!di) return { error: `Component "${kuikaId}" not found` };

      const oldName = di.friendlyName || di.title || "(unnamed)";
      di.friendlyName = newName;
      const screenDesign = sd.screenProvider.getScreenDesign();
      sd.screenProvider.setScreenDesign(screenDesign, 1);
      return {
        success: true,
        kuikaId,
        componentType: di.item?.name || "unknown",
        oldName,
        newName
      };
    }
  }
};
